import { Link } from "react-router";
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Globe, Key, MapPin, Thermometer, Train } from "lucide-react";

const features = [
  {
    icon: Globe,
    title: "8,000+ Airports & 229,000+ Cities",
    description: "Comprehensive geodata with IATA codes, coordinates, timezones, and population. Search, nearby, and direct lookup endpoints.",
  },
  {
    icon: Thermometer,
    title: "Climate Normals for 229,000+ Cities",
    description: "Monthly temperature, precipitation, and cloud cover from CHELSA v2.1 climatologies. Perfect for travel planning and destination comparison.",
  },
  {
    icon: MapPin,
    title: "Travel-Friendly Names",
    description: "City names match what travelers expect: Istanbul (not Arnavutköy), Tokyo (not Narita), Paris (not Roissy). Airports linked to cities.",
  },
  {
    icon: Train,
    title: "Berlin Transit Schedules",
    description: "Nearby stops, departures, route shapes, and line info from VBB GTFS data. U-Bahn, S-Bahn, tram, bus, ferry, and regional trains.",
  },
  {
    icon: Key,
    title: "One-Click API Keys",
    description: "Sign up, create a key, and make your first request in under a minute. No approval process, no waiting.",
  },
];

const codeExample = `# Airport lookup
curl -H "Authorization: Bearer td_your_key" \\
  https://travelapi.dev/v1/airports/IST

{ "iata": "IST", "name": "Istanbul Airport",
  "city": "Istanbul", "country": "TR",
  "cityId": 745044, "cityCode": "IST", ... }

# City lookup by IATA code
curl -H "Authorization: Bearer td_your_key" \\
  https://travelapi.dev/v1/cities/iata/NYC

{ "id": 5128581, "name": "New York City",
  "country": "US", "population": 8804190,
  "airports": ["EWR", "JFK", "LGA"], ... }

# Climate normals for Berlin
curl -H "Authorization: Bearer td_your_key" \\
  https://travelapi.dev/v1/weather/normals/2950159

{ "cityId": 2950159,
  "months": [{ "month": 1, "tmin": -2.0,
    "tmax": 2.7, "precipitation": 48.7,
    "cloudPercent": 55.7 }, ...] }

# Transit departures from Alexanderplatz
curl -H "Authorization: Bearer td_your_key" \\
  "https://travelapi.dev/v1/transit/stops/de:11000:900100003/departures?date=2026-05-18&time=14:30"

{ "departures": [{ "line": "U5",
    "direction": "S+U Hauptbahnhof",
    "departureTime": "14:32:00", ... }] }`;

const LandingPage = () => {
  return (
    <div>
      {/* Hero + Code Example */}
      <section className="py-20 sm:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                The simplest
                <br />
                travel data API
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-lg">
                Airports, cities, climate data, and Berlin transit schedules. All with one API key.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <SignedOut>
                  <Button size="lg" asChild>
                    <Link to="/sign-up">Get Your API Key</Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/docs">Try It in Your Browser</Link>
                  </Button>
                </SignedOut>
                <SignedIn>
                  <Button size="lg" asChild>
                    <Link to="/dashboard">Go to Dashboard</Link>
                  </Button>
                </SignedIn>
              </div>
            </div>
            <div>
              <pre className="bg-card border rounded-lg p-6 overflow-x-auto text-sm">
                <code>{codeExample}</code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Why Travel API?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {features.map((feature) => (
              <Card key={feature.title}>
                <CardHeader>
                  <feature.icon className="h-8 w-8 text-primary mb-2" />
                  <CardTitle>{feature.title}</CardTitle>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Free During Beta</h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
            100 requests per minute. No credit card.
          </p>
          <Card className="max-w-sm mx-auto">
            <CardHeader>
              <CardTitle className="text-2xl">Beta</CardTitle>
              <p className="text-3xl font-bold">$0</p>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-left">
              <div>✓ 100 requests per minute</div>
              <div>✓ 8,000+ airports & 229,000+ cities</div>
              <div>✓ Climate normals for 229,000+ cities</div>
              <div>✓ Berlin/Brandenburg transit schedules</div>
              <div>✓ Search, nearby, & direct lookup</div>
              <div>✓ Self-service key management</div>
            </CardContent>
          </Card>
          <div className="mt-8">
            <SignedOut>
              <Button size="lg" asChild>
                <Link to="/sign-up">Sign Up Free</Link>
              </Button>
            </SignedOut>
            <SignedIn>
              <Button size="lg" asChild>
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
            </SignedIn>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} Travel API</p>
            <div className="flex items-center gap-6">
              <Link to="/docs" className="hover:text-foreground transition-colors">Docs</Link>
              <a href="https://travelapi.dev" className="hover:text-foreground transition-colors">travelapi.dev</a>
              <span>Powered by Cloudflare Workers</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
